import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  minWords: "",
  maxWords: "",
  layout: "top-bottom",
  points: 10,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";
  const body = item?.body || {};
  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    minWords:
      typeof body.minWords === "number" && !Number.isNaN(body.minWords)
        ? String(body.minWords)
        : "",
    maxWords:
      typeof body.maxWords === "number" && !Number.isNaN(body.maxWords)
        ? String(body.maxWords)
        : "",
    layout: body.layout || "top-bottom",
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 10,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : "",
    explanation_fa,
    explanation_en,
  };
};

export default function EssayModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("essay.toasts.stem_required"));
      return;
    }

    const minW = form.minWords !== "" ? Number(form.minWords) : undefined;
    const maxW = form.maxWords !== "" ? Number(form.maxWords) : undefined;

    if (minW && maxW && minW > maxW) {
      toast.error(t("essay.toasts.min_gt_max"));
      return;
    }

    const payload = {
      kind: "essay",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        minWords: minW,
        maxWords: maxW,
        layout: form.layout,
        resources: [],
      },
      points: Number(form.points) || 0,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("essay.toasts.updated", "سؤال Essay با موفقیت ویرایش شد")
          : t("essay.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="mb-6 space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("essay.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("essay.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("essay.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("essay.stem_en_ph")}
            directionality="ltr"
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />
        </div>
      </div>

      {/* Layout Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("essay.layout_label")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`essay.layout_question_answer_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>

      {/* Word Limits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("essay.min_words")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("essay.min_words_ph")}
            value={form.minWords}
            onChange={onChange}
            name="minWords"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("essay.max_words")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("essay.max_words_ph")}
            value={form.maxWords}
            onChange={onChange}
            name="maxWords"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("essay.points")}
          </label>
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={onChange}
            name="points"
          />
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("essay.status")}
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={onChange}
            name="status"
          >
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            {t("essay.tags")}
          </label>
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={onChange}
            name="tags"
          />
        </div>
      </div>

      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>
    </form>
  );
}
